import cx from 'classnames';
import type { VoidFunctionComponent } from 'react';

import DropdownButton from '../DropdownButton';
import Icon, { icons } from '../Icon';
import { ICON_SIZE } from '../Icon/icon-constants';
import ReadOnlySection from '../ReadOnlySection';
import Tooltip from '../Tooltip';

import type { ResourceType } from './types';

import styles from './SavedResource.module.less';

export type SavedResourceActionType = 'Reset' | 'Save' | 'Save As...' | 'Rename...' | 'Delete';

export type SavedResourceAction = {
  /** The label to display for the action */
  label: string;
  /** The function to call when the action is selected */
  onSelect: () => void;
  /** The tooltip to display for the action */
  tooltip?: string;
  /** Whether the action is disabled */
  isDisabled?: boolean;
  /** Whether the action is restricted */
  isRestricted?: boolean;
};

export type SavedResourceDropdownActionsProps = {
  /** The type of resource, used to identify the resource in the UI. */
  resourceType: ResourceType;
  /** The actions to display in the dropdown */
  actions: SavedResourceAction[];
  /**  If a network request is happening, or when none of the options are available. */
  isDisabled: boolean;
  /** Whether the dropdown has a notification dot, usually for dirty state */
  hasNotificationDot?: boolean;
  /** Whether the user has any edit permission */
  hasAnyEditPermission: boolean;
  onChange: (selectedOption: SavedResourceActionType) => void;
};

/**
 * SavedResourceDropdownActions renders the actions for saving/deleting/etc.
 * a saved resource from in the UI.
 */
export const SavedResourceDropdownActions: VoidFunctionComponent<SavedResourceDropdownActionsProps> = ({
  resourceType,
  actions,
  isDisabled,
  hasAnyEditPermission,
  hasNotificationDot,
  onChange,
}) => (
  <Tooltip
    id={`saved-${resourceType}-actions-dropdown-tooltip`}
    placement="top"
    showTooltip={isDisabled || !hasAnyEditPermission}
    //  If the user is able to edit, the actions are disabled because the resources have not been edited.
    tooltip={
      hasAnyEditPermission
        ? `Modify ${resourceType}s in order to save them`
        : `You do not have permission to modify saved ${resourceType}s.`
    }
  >
    <DropdownButton
      id={`saved-${resourceType}-actions-dropdown`}
      title={<Icon icon={icons.sliders} size={ICON_SIZE.LG} />}
      disabled={isDisabled || !hasAnyEditPermission}
      size="large"
      onSelect={(e) => onChange(e as SavedResourceActionType)}
      hasNotificationDot={hasAnyEditPermission && hasNotificationDot}
      testId={`saved-${resourceType}-actions-dropdown`}
      noCaret
      pullRight
    >
      {actions.map(({ label, isDisabled: isActionDisabled, tooltip, isRestricted: isActionRestricted }) => (
        <DropdownButton.Item key={label} eventKey={label} disabled={isActionDisabled && !isActionRestricted}>
          <Tooltip
            id={`saved-${resourceType}-action-tooltip--${label}`}
            placement="top"
            showTooltip={!!tooltip && isActionDisabled && !isActionRestricted}
            tooltip={tooltip}
          >
            <div
              className={cx(styles.actionItem, {
                [styles.deleteAction]: !isActionDisabled && !isActionRestricted && label === 'Delete',
              })}
              data-test-id={`saved-${resourceType}-action--${label}`}
            >
              <ReadOnlySection isReadOnly={isActionRestricted} isClickable>
                {label}
              </ReadOnlySection>
            </div>
          </Tooltip>
          {isActionRestricted && <Icon icon={icons.lock} />}
        </DropdownButton.Item>
      ))}
    </DropdownButton>
  </Tooltip>
);
